(() => {
  'use strict';

  const submittedSessionKey = 'vd-contact-successful-submit';

  function isSuccessfulContactPage() {
    const params = new URLSearchParams(window.location.search);
    return params.get('contact_posted') === 'true';
  }

  function forceScrollToTop() {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }

    const scrollToTop = () => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    };

    scrollToTop();

    requestAnimationFrame(() => {
      scrollToTop();
      requestAnimationFrame(scrollToTop);
    });

    window.setTimeout(scrollToTop, 80);
    window.setTimeout(scrollToTop, 250);
  }

  function cleanSuccessHash() {
    if (!isSuccessfulContactPage() || !window.location.hash) return;

    const cleanUrl = `${window.location.pathname}${window.location.search}`;

    try {
      history.replaceState(history.state, document.title, cleanUrl);
    } catch (error) {
      console.warn('[VitaDoza Contact] Success URL hash could not be removed:', error);
    }
  }

  function restoreSuccessPageToTop() {
    if (!isSuccessfulContactPage()) return;

    // Shopify appends #VdContactForm-... after a successful contact post.
    // Remove that anchor first so the browser cannot scroll back to the form.
    cleanSuccessHash();
    forceScrollToTop();
  }

  function isBackForwardNavigation(event) {
    if (event && event.persisted) return true;

    const entries = performance.getEntriesByType
      ? performance.getEntriesByType('navigation')
      : [];

    return Boolean(entries[0] && entries[0].type === 'back_forward');
  }

  function clearContactForm(root) {
    if (!root) return;

    const form = root.querySelector('.vd-contact__form');
    if (!form) return;

    form.querySelectorAll('input, textarea, select').forEach((field) => {
      if (
        field instanceof HTMLInputElement &&
        ['hidden', 'submit', 'button', 'reset'].includes(field.type)
      ) {
        return;
      }

      if (field instanceof HTMLInputElement) {
        if (field.type === 'checkbox' || field.type === 'radio') {
          field.checked = false;
        } else {
          field.value = '';
        }

        field.setCustomValidity('');
        field.removeAttribute('aria-invalid');
      } else if (field instanceof HTMLTextAreaElement) {
        field.value = '';
        field.setCustomValidity('');
        field.removeAttribute('aria-invalid');
      } else if (field instanceof HTMLSelectElement) {
        field.selectedIndex = 0;
        field.removeAttribute('aria-invalid');
      }
    });

    const phoneError = root.querySelector('[data-vd-phone-error]');
    if (phoneError) phoneError.hidden = true;

    const privacyError = root.querySelector('[data-vd-privacy-error]');
    if (privacyError) privacyError.hidden = true;

    const consentWrap = root.querySelector('.vd-contact__consent-wrap');
    if (consentWrap) consentWrap.classList.remove('is-error');

    const subject = root.querySelector('[data-vd-subject]');
    if (subject) {
      subject.dispatchEvent(new Event('change', { bubbles: true }));
    }

    const orderInput = root.querySelector('[data-vd-order-input]');
    if (orderInput && orderInput.disabled) {
      orderInput.value = '';
    }
  }

  function clearFormAfterSuccessfulBackNavigation(event) {
    if (!isBackForwardNavigation(event)) return;

    let shouldClear = false;

    try {
      shouldClear = sessionStorage.getItem(submittedSessionKey) === 'true';
    } catch (error) {
      console.warn('[VitaDoza Contact] Session storage unavailable:', error);
    }

    if (!shouldClear) return;

    document.querySelectorAll('[data-vd-contact]').forEach(clearContactForm);

    try {
      sessionStorage.removeItem(submittedSessionKey);
    } catch (error) {
      console.warn('[VitaDoza Contact] Session storage cleanup failed:', error);
    }

    // A browser Back navigation can also restore the previous scroll position
    // from BFCache. Always start the clean form page from the top.
    forceScrollToTop();
  }

  function initContact(root) {
    if (!root || root.dataset.vdContactReady === 'true') return;
    root.dataset.vdContactReady = 'true';

    const successState = root.querySelector('.vd-contact__success');
    root.classList.toggle('vd-contact--success', Boolean(successState));

    const form = root.querySelector('.vd-contact__form');
    const privacyCheckbox = root.querySelector('[data-vd-privacy-checkbox]');
    const privacyError = root.querySelector('[data-vd-privacy-error]');
    const consentWrap = root.querySelector('.vd-contact__consent-wrap');

    const phoneInput = root.querySelector('[data-vd-phone]');
    const phoneError = root.querySelector('[data-vd-phone-error]');

    const subject = root.querySelector('[data-vd-subject]');
    const orderRow = root.querySelector('[data-vd-order-row]');
    const orderInput = root.querySelector('[data-vd-order-input]');

    const modal = root.querySelector('[data-vd-modal]');
    const openers = root.querySelectorAll('[data-vd-modal-open]');

    function normalizePhoneValue(rawValue) {
      const original = (rawValue || '').trim();
      if (!original) return { valid: true, formatted: '', normalized: '' };

      let compact = original.replace(/[()\s.\-]/g, '');

      if (compact.startsWith('00')) {
        compact = `+${compact.slice(2)}`;
      }

      // Turkey: accept 0532..., 532..., +90532...
      if (/^05\d{9}$/.test(compact)) {
        compact = `+90${compact.slice(1)}`;
      } else if (/^5\d{9}$/.test(compact)) {
        compact = `+90${compact}`;
      }

      // International structural validation, compatible with E.164 length limit.
      if (!/^\+[1-9]\d{7,14}$/.test(compact)) {
        return { valid: false, formatted: original, normalized: compact };
      }

      const digits = compact.slice(1);

      if (compact.startsWith('+90') && digits.length === 12) {
        const national = digits.slice(2);
        return {
          valid: true,
          normalized: compact,
          formatted: `+90 ${national.slice(0, 3)} ${national.slice(3, 6)} ${national.slice(6, 8)} ${national.slice(8, 10)}`
        };
      }

      return { valid: true, normalized: compact, formatted: compact };
    }

    function showPhoneError() {
      if (!phoneInput || !phoneError) return;
      phoneError.hidden = false;
      phoneInput.setAttribute('aria-invalid', 'true');
    }

    function clearPhoneError() {
      if (!phoneInput || !phoneError) return;
      phoneError.hidden = true;
      phoneInput.removeAttribute('aria-invalid');
    }

    function validatePhone({ format = false, focus = false } = {}) {
      if (!phoneInput) return true;

      const result = normalizePhoneValue(phoneInput.value);

      if (!result.valid) {
        showPhoneError();
        phoneInput.setCustomValidity(
          phoneError ? phoneError.textContent.trim() : 'Geçersiz telefon numarası.'
        );

        if (focus) {
          phoneInput.focus({ preventScroll: true });
          phoneInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return false;
      }

      clearPhoneError();
      phoneInput.setCustomValidity('');

      if (format && result.formatted) {
        phoneInput.value = result.formatted;
      }

      return true;
    }

    if (phoneInput) {
      phoneInput.addEventListener('blur', () => {
        if (phoneInput.value.trim()) validatePhone({ format: true });
      });

      phoneInput.addEventListener('input', () => {
        if (phoneInput.getAttribute('aria-invalid') === 'true') {
          validatePhone();
        }
      });
    }

    function showPrivacyError() {
      if (!privacyCheckbox || !privacyError) return;
      privacyError.hidden = false;
      if (consentWrap) consentWrap.classList.add('is-error');
      privacyCheckbox.setAttribute('aria-invalid', 'true');
    }

    function clearPrivacyError() {
      if (!privacyCheckbox || !privacyError) return;
      privacyError.hidden = true;
      if (consentWrap) consentWrap.classList.remove('is-error');
      privacyCheckbox.removeAttribute('aria-invalid');
    }

    if (privacyCheckbox) {
      privacyCheckbox.addEventListener('invalid', (event) => {
        event.preventDefault();
        showPrivacyError();
        privacyCheckbox.focus({ preventScroll: true });
        privacyCheckbox.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });

      privacyCheckbox.addEventListener('change', () => {
        if (privacyCheckbox.checked) clearPrivacyError();
      });

      privacyCheckbox.addEventListener('click', () => {
        if (privacyCheckbox.checked) clearPrivacyError();
      });
    }

    if (form) {
      const nameInput = form.querySelector(
        '[name="contact[name]"], [name="name_surname"], [name="name"]'
      );
      const emailInput = form.querySelector(
        '[name="contact[email]"], [name="email"]'
      );
      const messageInput = form.querySelector(
        '[name="contact[body]"], [name="message"]'
      );

      function sendSubmissionToApp() {
        const selectedSubject =
          subject && subject.options && subject.selectedIndex >= 0
            ? subject.options[subject.selectedIndex]
            : null;

        const payload = {
          name_surname: nameInput ? nameInput.value.trim() : '',
          email: emailInput ? emailInput.value.trim() : '',
          phone: phoneInput ? phoneInput.value.trim() : '',
          subject: selectedSubject ? selectedSubject.value.trim() : '',
          order_number:
            orderInput && !orderInput.disabled ? orderInput.value.trim() : '',
          message: messageInput ? messageInput.value.trim() : '',
          privacy_consent: Boolean(privacyCheckbox && privacyCheckbox.checked)
        };

        fetch('/apps/vitadoza-contact', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          credentials: 'same-origin',
          keepalive: true,
          body: JSON.stringify(payload)
        })
          .then(async (response) => {
            const data = await response.json().catch(() => null);
            if (!response.ok || !data || data.ok !== true) {
              console.error('[VitaDoza Contact] App submission failed:', response.status, data);
              return;
            }
            console.info('[VitaDoza Contact] Submission saved:', data.request_id);
          })
          .catch((error) => {
            console.error('[VitaDoza Contact] App submission error:', error);
          });
      }

      form.addEventListener('submit', (event) => {
        const phoneValid = validatePhone({ format: true, focus: true });

        if (!phoneValid) {
          event.preventDefault();
          return;
        }

        if (privacyCheckbox && privacyCheckbox.required && !privacyCheckbox.checked) {
          event.preventDefault();
          showPrivacyError();
          privacyCheckbox.focus({ preventScroll: true });
          privacyCheckbox.scrollIntoView({ behavior: 'smooth', block: 'center' });
          return;
        }

        try {
          sessionStorage.setItem(submittedSessionKey, 'true');
        } catch (error) {
          console.warn('[VitaDoza Contact] Session storage unavailable:', error);
        }

        sendSubmissionToApp();
      });
    }

    function shouldShowOrderField(option) {
      if (!option || !option.value) return false;
      return option.dataset.showOrder === 'show';
    }

    function updateOrderField() {
      if (!subject || !orderRow) return;

      const selected = subject.options[subject.selectedIndex];
      const shouldShow = shouldShowOrderField(selected);

      orderRow.hidden = !shouldShow;
      orderRow.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');

      if (orderInput) {
        orderInput.disabled = !shouldShow;
        if (!shouldShow) orderInput.value = '';
      }
    }

    if (subject && orderRow) {
      subject.addEventListener('change', updateOrderField);
      updateOrderField();
    }


    const mapEl = root.querySelector('[data-vd-map]');

    function showMapFallback(element) {
      if (!element) return;
      const fallback = element.querySelector('[data-vd-map-fallback]');
      if (fallback) fallback.hidden = false;
    }

    function getMapStyles(styleName) {
      const styles = {
        default: null,
        silver: [
          { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
          { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
          { featureType: "administrative", elementType: "labels.text.fill", stylers: [{ color: "#8b8b8b" }] },
          { featureType: "poi", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
          { featureType: "poi.park", elementType: "geometry", stylers: [{ color: "#e5e5e5" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
          { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#dadada" }] },
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9c9c9" }] }
        ],
        retro: [
          { elementType: "geometry", stylers: [{ color: "#ebe3cd" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#523735" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#f5f1e6" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#f5f1e6" }] },
          { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#f8c967" }] },
          { featureType: "water", elementType: "geometry.fill", stylers: [{ color: "#b9d3c2" }] }
        ],
        dark: [
          { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
          { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
          { elementType: "labels.text.fill", stylers: [{ color: "#9ca5b3" }] },
          { featureType: "road", elementType: "geometry", stylers: [{ color: "#38414e" }] },
          { featureType: "water", elementType: "geometry", stylers: [{ color: "#17263c" }] }
        ]
      };

      return styles[styleName] || styles.default;
    }

    function loadGoogleMapsApi(apiKey) {
      if (window.google && window.google.maps) {
        return Promise.resolve(window.google.maps);
      }

      if (window.__vdGoogleMapsPromise) {
        return window.__vdGoogleMapsPromise;
      }

      window.__vdGoogleMapsPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector('script[data-vd-google-maps]');
        if (existing) {
          existing.addEventListener('load', () => resolve(window.google.maps), { once: true });
          existing.addEventListener('error', reject, { once: true });
          return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
        script.async = true;
        script.defer = true;
        script.dataset.vdGoogleMaps = 'true';
        script.onload = () => {
          if (window.google && window.google.maps) {
            resolve(window.google.maps);
          } else {
            reject(new Error('Google Maps API loaded without maps namespace.'));
          }
        };
        script.onerror = () => {
          window.__vdGoogleMapsPromise = null;
          reject(new Error('Google Maps script failed to load.'));
        };
        document.head.appendChild(script);
      });

      return window.__vdGoogleMapsPromise;
    }

    async function initMap(element) {
      if (!element || element.dataset.vdMapReady === 'true') return;

      const apiKey = (element.dataset.apiKey || '').trim();
      const address = (element.dataset.address || '').trim();
      const zoom = Number.parseInt(element.dataset.zoom || '15', 10);
      const styleName = element.dataset.style || 'silver';
      const canvas = element.querySelector('[data-vd-map-canvas]');

      if (!apiKey || !address || !canvas) {
        showMapFallback(element);
        return;
      }

      element.dataset.vdMapReady = 'true';

      try {
        await loadGoogleMapsApi(apiKey);

        const geocoder = new google.maps.Geocoder();

        geocoder.geocode({ address }, (results, status) => {
          if (status !== 'OK' || !results || !results[0]) {
            element.dataset.vdMapReady = 'false';
            showMapFallback(element);
            console.error('[VitaDoza Contact] Google Maps geocode error:', status);
            return;
          }

          const position = results[0].geometry.location;

          const map = new google.maps.Map(canvas, {
            center: position,
            zoom: Number.isNaN(zoom) ? 15 : zoom,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            rotateControl: false,
            scaleControl: false,
            zoomControl: true,
            gestureHandling: 'cooperative',
            styles: getMapStyles(styleName)
          });

          new google.maps.Marker({
            position,
            map,
            title: address
          });
        });
      } catch (error) {
        element.dataset.vdMapReady = 'false';
        showMapFallback(element);
        console.error('[VitaDoza Contact] Google Maps load error:', error);
      }
    }

    if (mapEl) {
      initMap(mapEl);
    }

    if (!modal || !openers.length) return;

    const dialog = modal.querySelector('.vd-contact__modal-dialog');
    const overlay = modal.querySelector('[data-vd-modal-overlay]');
    const closers = modal.querySelectorAll('[data-vd-modal-close]');
    const closeOnOverlay = root.dataset.modalCloseOverlay === 'true';

    let lastFocused = null;

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    function getFocusable() {
      return Array.from(dialog.querySelectorAll(focusableSelector))
        .filter((el) => !el.hasAttribute('hidden') && el.offsetParent !== null);
    }

    function openModal(event) {
      if (event) event.preventDefault();
      lastFocused = document.activeElement;
      modal.hidden = false;
      document.body.classList.add('vd-contact-modal-open');

      requestAnimationFrame(() => {
        dialog.focus({ preventScroll: true });
      });
    }

    function closeModal() {
      modal.hidden = true;
      document.body.classList.remove('vd-contact-modal-open');

      if (lastFocused && typeof lastFocused.focus === 'function') {
        lastFocused.focus({ preventScroll: true });
      }
    }

    function handleKeydown(event) {
      if (modal.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeModal();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusable = getFocusable();

      if (!focusable.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    openers.forEach((opener) => opener.addEventListener('click', openModal));
    closers.forEach((closer) => closer.addEventListener('click', closeModal));

    if (overlay && closeOnOverlay) {
      overlay.addEventListener('click', closeModal);
    }

    document.addEventListener('keydown', handleKeydown);

    root._vdContactCleanup = () => {
      document.removeEventListener('keydown', handleKeydown);
      document.body.classList.remove('vd-contact-modal-open');
    };
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-vd-contact]').forEach(initContact);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      restoreSuccessPageToTop();
      initAll();
    });
  } else {
    restoreSuccessPageToTop();
    initAll();
  }

  window.addEventListener('load', restoreSuccessPageToTop);

  window.addEventListener('pageshow', (event) => {
    if (isSuccessfulContactPage()) {
      restoreSuccessPageToTop();
      return;
    }

    clearFormAfterSuccessfulBackNavigation(event);
  });

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-vd-contact]');
    if (root && typeof root._vdContactCleanup === 'function') {
      root._vdContactCleanup();
    }
  });
})();
